<template>
	<div class="c-calendar-components">
		<div class="loader" v-if="isLoading"><i class="fal fa-circle-notch fa-spin" aria-hidden="true"></i> Loading...</div>

		<section class="c-calendar-filters">
			<div>
				<button v-on:click="toggleFilterDropdown" id="c-calendar-filters-toggle" class="c-calendar-filters-toggle" aria-expanded="false" aria-controls="c-calendar-filters-group" >
					Filter events 
					<i class="fas fa-caret-down" aria-hidden="true"></i>
				</button>
			</div>
			
			<div id="c-calendar-filters-group" class="c-calendar-filters__wrapper">
				<DateNavigation
					:next-button="false"
					:previous-button="false"
					:first-instance="firstInstance"
					:last-instance="lastInstance"
				/>

				<EventTypeFilter/>

				<ProductionFilter/>

				<AccessibilityFilter/>

				<div class="c-calendar-filters__results">
					<a href="#fullCalendar" class="c-btn c-btn--primary c-btn--small"> View Results </a>
				</div>

			</div>

		</section>

		<div class="c-calendar-filters__clear">
			<FilterOutput
				v-if="this.filterText.length"
				:filter-text="this.filterText"
			/>
		</div>
		
		<FullCalendar
			id="fullCalendar"
			ref='fullCalendar'
			:options="calendarOptions"
		/>
	</div>
</template>

<script>
	import FullCalendar from '@fullcalendar/vue'
	import momentPlugin from '@fullcalendar/moment'
	import moment from 'moment'
	import dayGridPlugin from '@fullcalendar/daygrid'
	import listPlugin from '@fullcalendar/list'
	import DateNavigation from "./DateNavigation"
	import EventTypeFilter from "./EventTypeFilter"
	import ProductionFilter from "./ProductionFilter"
	import AccessibilityFilter from "./AccessibilityFilter"
	import FilterOutput from "./FilterOutput"
	import {EventBus} from "../event-bus"
	import axios from 'axios'
	import debounce from 'lodash.debounce'

	export default {
		name: 'Calendar',
		props: ['firstInstance', 'lastInstance'],
		components: {
			FullCalendar,
			DateNavigation,
			EventTypeFilter,
			ProductionFilter,
			AccessibilityFilter,
			FilterOutput
		},
		data() {
			return {
				calendarOptions: {
					firstDay: 1,
					headerToolbar: {
						start: 'prev,title,next',
						end: ''
					},
					buttonIcons: false,
					buttonText: {
						prev: '',
						next: '',
					},
					dayMaxEventRows: true,
					views: {
						dayGridMonth: {
							dayMaxEventRows: 6
						},
						listMonth: {
							dayMaxEventRows: 6
						},
					},
					footerToolbar: false,
					height: 'auto',
					plugins: [dayGridPlugin, listPlugin, momentPlugin],
					initialView: this.setInitialView(),
					events: [],
					displayEventTime: false,
					eventContent: this.eventContent,
					listDayFormat: 'dddd DD MMMM YYYY',
					listDaySideFormat: false,
					validRange: this.validRange,
					fixedWeekCount: false,
					timeZone: 'UTC'
				},
				isLoading: true,
				allInstances: {},
				calendarApi: null,
				currentView: this.setInitialView(),
				filters: {},
				filterText: '',
				filterDropdownActive: false,
			}
		},
		mounted() {
			this.addEventListeners()
			this.calendarApi = this.$refs.fullCalendar.getApi()
			// This check avoids requesting for events twice on jump to month requests
			if (!this.urlParamJumpMonth()) {
				this.fetchEvents()
			}
			this.configNextAndPrevBtns()
		},
		created() {
			this.currentView = this.setInitialView()
		},
		methods: {
			addEventListeners() {
				EventBus.$on('next', this.nextMonth)
				EventBus.$on('previous', this.previousMonth)
				EventBus.$on('goToMonth', this.goToMonth)
				EventBus.$on('updateEventFilters', this.updateFilters)
				EventBus.$on('clearFilters', this.clearFilters)
				
				this.nextAndPreviousButtonAdditionalEvents()
				window.addEventListener('resize', debounce(this.onResizeUpdateView, 200))
			},
			clearFilters() {
				this.filters = {}
				this.fetchEvents()
			},
			configNextAndPrevBtns() {
				const previousButton = document.querySelector('.fc-prev-button')
				previousButton.removeAttribute('title')
				previousButton.innerHTML = '<span class="u-hidden-visually">Go to previous month</span><i class="fas fa-caret-left aria-hidden="true"></i>'
				
				const nextButton = document.querySelector('.fc-next-button')
				nextButton.removeAttribute('title')
				nextButton.innerHTML = '<span class="u-hidden-visually">Go to next month</span><i class="fas fa-caret-right" aria-hidden="true"></i>'
				const chevronLeft = document.querySelector('.fc-icon-chevron-left')
				if (chevronLeft) {
					nextButton.removeChild(chevronLeft)
				}
			}, 
			eventContent(arg) {
				return {
					html: arg.event.title
				}
			},
			fetchEvents() {
				this.isLoading = true;
				// Otherwise, fetch the instances
				let params = this.setupQueryParams();

				axios.get(window.ajax.url, {
					params: params,
				}).then((response) => {
					// Assign the instances to our property as that is passed to the calendar
					this.calendarOptions.events = response.data.results;
					this.filterText = response.data.filterText;
					this.isLoading = false;
				}).catch((error) => {
					this.filterText = 'Oops! An issue has occured while trying to load the calendar events. Please try again.'
					this.isLoading = false;
					console.log(error);
				});
			},
			goToMonth(selectedMonth) {
				let month = moment(new Date(selectedMonth)).utc().startOf('month')
				this.calendarApi.gotoDate(month.format('YYYY-MM-DD'))
				this.fetchEvents()
				this.updateMonth(month.format('YYYY-MM-DD'), 'goToMonth')
			},
			nextAndPreviousButtonAdditionalEvents() {
				// previous button
				this.on('body', 'click', '.fc-prev-button', (e) => {
					this.updateMonth(moment(this.calendarApi.currentData.currentDate).utc().format(), 'prev')
					this.fetchEvents()
				})
				
				// next button
				this.on('body', 'click', '.fc-next-button', (e) => {
					console.log('fc-next-button')
					this.updateMonth(moment(this.calendarApi.currentData.currentDate).utc().format(), 'next')
					this.fetchEvents()
				})
			},
			nextMonth() {
				this.calendarApi.next()
				this.updateMonth(this.calendarApi.currentData.currentDate, 'next')
			},
			onResizeUpdateView() {
				const view = window.innerWidth < 1040 ? 'listMonth' : 'dayGridMonth'
				this.currentView = view
				this.calendarApi.changeView(view)
				this.fetchEvents()
			},
			previousMonth() {
				this.calendarApi.prev()
				this.updateMonth(this.calendarApi.currentData.currentDate, 'previous')
			},
			setInitialView() {
				// List view on tablet and mobile
				if (window.screen.width < 1040) {
					return 'listMonth'
				}
				
				return 'dayGridMonth'
			},
			setupQueryParams() {
				let params = {
					action: 'basethemeCalendarRequest',
					start: moment.utc(this.calendarApi.currentData.currentDate).format(),
					end: moment.utc(this.calendarApi.currentData.currentDate).endOf("month").format(),
					filters: this.filters
				}

				return params
			},
			urlParamJumpMonth() {
				const urlParams = new URLSearchParams(window.location.search)
				
				if (urlParams.has('month')) {
					const selectedMonth = urlParams.get('month')
					this.goToMonth(selectedMonth)
					return true
				}
				
				return false
			},
			updateFilters(name, value) {
				this.filters[name] = value
				this.fetchEvents()
			},
			updateMonth(month, button) {
				EventBus.$emit('updateMonth', month, button)
			},
			validRange(nowDate) {
				if (this.calendarApi && this.calendarApi.currentData.viewApi.type === "listMonth") {
					return {
						start: this.firstInstance !== undefined ? this.firstInstance : nowDate,
						end: this.lastInstance !== undefined ? this.lastInstance : false,
					}
				}
			},
			on(selector, eventType, childSelector, eventHandler) {
				const elements = document.querySelectorAll(selector)
				for (let element of elements) {
					element.addEventListener(eventType, eventOnElement => {
						if (eventOnElement.target.matches(childSelector)) {
							eventHandler(eventOnElement)
						}
					})
				}
			},
			toggleFilterDropdown(e) {
				const filterToggle = e.target;
				const html = document.querySelector("html");

				this.filterDropdownActive = !this.filterDropdownActive

				if(this.filterDropdownActive){
					html.classList.add('state--filters-active');
					filterToggle.setAttribute('aria-expanded', true);
				}else {
					html.classList.remove('state--filters-active');
					filterToggle.setAttribute('aria-expanded', false);
				}
			}
		},
	}
</script>

<style lang='css'>
@import '~@fullcalendar/common/main.css';
@import '~@fullcalendar/daygrid/main.css';
@import '~@fullcalendar/list/main.css';
</style>
